import React, { useState } from 'react';
import { MapPin, Phone, Mail, Clock, Send } from 'lucide-react';
import { centerInfo } from '../utils/mockData';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    course: '',
    message: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await axios.post(`${API}/contact-submissions`, formData);
      
      if (response.data) {
        toast.success('Message envoyé avec succès!', {
          description: 'Nous vous contacterons sous 24 heures.'
        });
        setFormData({
          name: '',
          email: '',
          phone: '',
          course: '',
          message: ''
        });
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error('Erreur lors de l\'envoi', {
        description: 'Veuillez réessayer plus tard.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactInfo = [
    {
      icon: MapPin,
      title: 'Adresse',
      info: centerInfo.address,
      color: 'bg-[#007A5E]'
    },
    {
      icon: Phone,
      title: 'Téléphone',
      info: centerInfo.phone,
      link: `tel:${centerInfo.phone}`,
      color: 'bg-[#0039A6]'
    },
    {
      icon: Mail,
      title: 'Email',
      info: centerInfo.email,
      link: `mailto:${centerInfo.email}`,
      color: 'bg-[#D52B1E]'
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-[#0039A6] to-[#007A5E] text-white py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">Contactez-Nous</h1>
            <p className="text-xl text-white/90">
              Nous sommes là pour répondre à toutes vos questions et vous aider à démarrer votre parcours linguistique
            </p>
          </div>
        </div>
      </section>

      {/* Contact Info Cards */}
      <section className="py-12 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto -mt-24 relative z-10">
            {contactInfo.map((item, index) => {
              const Icon = item.icon;
              return (
                <Card key={index} className="text-center hover:shadow-xl transition-shadow">
                  <CardContent className="pt-8">
                    <div className={`w-16 h-16 ${item.color} rounded-full flex items-center justify-center mx-auto mb-4`}>
                      <Icon className="text-white" size={28} />
                    </div>
                    <h3 className="font-bold text-lg mb-2 text-gray-900">{item.title}</h3>
                    {item.link ? (
                      <a
                        href={item.link}
                        className="text-gray-600 hover:text-[#0039A6] transition-colors break-all"
                      >
                        {item.info}
                      </a>
                    ) : (
                      <p className="text-gray-600">{item.info}</p>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Contact Form & Hours */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12 max-w-6xl mx-auto">
            {/* Contact Form */}
            <div className="lg:col-span-2">
              <Card>
                <CardHeader>
                  <CardTitle className="text-3xl text-[#0039A6]">
                    Formulaire de Contact
                  </CardTitle>
                  <p className="text-gray-600 mt-2">
                    Remplissez le formulaire ci-dessous et nous vous répondrons dans les plus brefs délais
                  </p>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <Label htmlFor="name">Nom Complet *</Label>
                        <Input
                          id="name"
                          name="name"
                          type="text"
                          required
                          value={formData.name}
                          onChange={handleChange}
                          placeholder="Votre nom"
                          className="border-gray-300 focus:border-[#0039A6] focus:ring-[#0039A6]"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="email">Email *</Label>
                        <Input
                          id="email"
                          name="email"
                          type="email"
                          required
                          value={formData.email}
                          onChange={handleChange}
                          placeholder="votre@email.com"
                          className="border-gray-300 focus:border-[#0039A6] focus:ring-[#0039A6]"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <Label htmlFor="phone">Téléphone *</Label>
                        <Input
                          id="phone"
                          name="phone"
                          type="tel"
                          required
                          value={formData.phone}
                          onChange={handleChange}
                          placeholder="+237 6XX XX XX XX"
                          className="border-gray-300 focus:border-[#0039A6] focus:ring-[#0039A6]"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="course">Cours d'intérêt</Label>
                        <select
                          id="course"
                          name="course"
                          value={formData.course}
                          onChange={handleChange}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#0039A6] focus:border-[#0039A6]"
                        >
                          <option value="">Sélectionner un cours</option>
                          <option value="A1">Russe Débutant A1</option>
                          <option value="A2">Russe Élémentaire A2</option>
                          <option value="B1">Russe Intermédiaire B1</option>
                          <option value="B2-C1">Russe Avancé B2-C1</option>
                          <option value="business">Russe des Affaires</option>
                          <option value="torfl">Préparation TORFL</option>
                        </select>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="message">Message *</Label>
                      <Textarea
                        id="message"
                        name="message"
                        required
                        value={formData.message}
                        onChange={handleChange}
                        placeholder="Parlez-nous de vos objectifs d'apprentissage..."
                        rows={6}
                        className="border-gray-300 focus:border-[#0039A6] focus:ring-[#0039A6] resize-none"
                      />
                    </div>

                    <Button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full bg-[#D52B1E] hover:bg-[#b82418] text-white py-6 text-lg"
                    >
                      {isSubmitting ? (
                        'Envoi en cours...'
                      ) : (
                        <>
                          Envoyer le message
                          <Send className="ml-2" size={18} />
                        </>
                      )}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </div>

            {/* Hours & Additional Info */}
            <div className="space-y-6">
              <Card>
                <CardHeader>
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-[#FCD116] rounded-lg flex items-center justify-center">
                      <Clock className="text-[#0039A6]" size={24} />
                    </div>
                    <CardTitle className="text-xl">Heures d'Ouverture</CardTitle>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {Object.entries(centerInfo.hours).map(([day, hours]) => (
                      <div key={day} className="flex justify-between items-center py-2 border-b border-gray-100 last:border-0">
                        <span className="font-medium text-gray-700 capitalize">{day}</span>
                        <span className="text-gray-600">{hours}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-gradient-to-br from-[#0039A6] to-[#007A5E] text-white">
                <CardContent className="pt-6">
                  <h3 className="text-xl font-bold mb-4">Cours d'Essai Gratuit</h3>
                  <p className="text-white/90 mb-4">
                    Assistez à un cours gratuit pour découvrir notre méthode d'enseignement
                  </p>
                  <Button className="w-full bg-white text-[#0039A6] hover:bg-gray-100">
                    Réserver maintenant
                  </Button>
                </CardContent>
              </Card>

              <Card className="bg-[#FCD116]">
                <CardContent className="pt-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-3">
                    Besoin d'aide immédiate ?
                  </h3>
                  <p className="text-gray-700 mb-4">
                    Appelez-nous directement pour une réponse rapide
                  </p>
                  <a
                    href={`tel:${centerInfo.phone}`}
                    className="block w-full bg-[#0039A6] text-white text-center py-3 rounded-lg font-medium hover:bg-[#0052d4] transition-colors"
                  >
                    {centerInfo.phone}
                  </a>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-3xl font-bold text-center text-gray-900 mb-8">
              Trouvez-nous à Douala
            </h2>
            <div className="bg-white rounded-lg shadow-lg overflow-hidden" style={{ height: '450px' }}>
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3979.8627!2d9.7084!3d4.0511!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zNMKwMDMnMDQuMCJOIDnCsDQyJzMwLjIiRQ!5e0!3m2!1sfr!2scm!4v1234567890"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Centre Linguistique Russe du Cameroun Location"
              ></iframe>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
